/**
 * AdditionalViewTab — F22 (widoki dodatkowe, PHASE_22_PLAN.md §8), wariant READ-ONLY
 * siostrzany do `AdditionalTableTab.tsx` (tabele dodatkowe — edytowalne siatki wierszy).
 * Ten komponent NIE edytuje/dodaje wierszy — renderuje wynik zapytania SQL zwrócony przez
 * `POST /settings/additional-views/:viewId/data` (`useAdditionalViewData`), kolumny
 * formatowane wg `AdditionalViewColumn.displayType`.
 *
 * Rozwiązywanie `viewId` ze slugu: mirror `AdditionalTableTab` — pobiera CAŁY katalog
 * widoków (`useAdditionalViewsList`, cache TanStack domyślny) i szuka po `slug` lokalnie
 * (katalog per tenant jest mały).
 *
 * Stan pusty „uzupełnij wymagane pola" (PHASE_22_PLAN.md §8): dla każdego zadeklarowanego
 * parametru WYMAGANEGO sprawdzamy, czy jego wiązanie da się rozwiązać z danych dostępnych
 * po stronie klienta:
 *  - `record_field` — wymaga niepustej wartości w `recordValues[fieldSlug]`.
 *  - `context.company`/`context.user`/`constant` — zawsze „rozwiązane" z punktu widzenia
 *    klienta (serwer je rozstrzyga; klient nie ma jak zweryfikować z wyprzedzeniem).
 *  - brak wiązania dla parametru wymaganego — traktowane jak brak wartości (ten sam
 *    komunikat — użytkownik i tak nie rozróżni „widok źle skonfigurowany" od „formularz
 *    niekompletny").
 *
 * Auto-refresh przy zmianie pola-parametru jest ŚWIADOMIE poza v1 (PHASE_22_PLAN.md §11).
 * `recordValues` użyte do zapytania to ZATWIERDZONY snapshot (`committedValues`) wzięty przy
 * MOUNCIE komponentu, odświeżany WYŁĄCZNIE przyciskiem „Odśwież" — nie na każdą zmianę
 * propsa `recordValues` (live, kontrolowany przez formularz rodzica). Tryb create, w którym
 * rekord dostaje id po zapisie, przechodzi przez redirect na nowy URL rekordu (konwencja
 * już istniejąca w ContractorDetail/EmployeeDetail/stronach dokumentów) — to naturalnie
 * remountuje ten komponent, więc nie trzeba osobnej synchronizacji na zmianę `recordId`.
 *
 * Układ kolumn (`columnSettings` = `element.settings` umieszczenia widoku na formatce): tabela
 * rysuje wyłącznie kolumny widoczne wg `resolveEmbeddedTableColumns` (`@saas/shared`), w kolejności
 * układu, z własnymi podpisami, szerokościami startowymi, wyrównaniem i miejscami po przecinku kwot.
 * Gdy układ ukrył wszystkie kolumny — komunikat zamiast pustej tabeli. Bez `columnSettings` rysunek
 * jest taki jak przed wprowadzeniem układu.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type AdditionalViewColumn,
  type AdditionalViewDefinition,
  effectiveColumnAlign,
  resolveEmbeddedTableColumns,
} from '@saas/shared';
import { LayoutGrid, RefreshCw } from 'lucide-react';

import { Button } from '@/components/Button';
import type { ColumnDefinition } from '@/components/configurable-table';
import { ConfigurableTable } from '@/components/configurable-table';
import type { ColumnFilterCategory } from '@/components/configurable-table/types';
import { Spinner } from '@/components/Spinner';
import { useAdditionalViewData } from '@/hooks/useAdditionalViewData';
import { useAdditionalViewsList } from '@/lib/additional-views/api';
import { buildAdditionalViewRecordValues } from '@/lib/additional-views/record-values';
import { additionalViewDescriptors } from '@/lib/embedded-table/additional-view-descriptors';
import { formatDecimalCell } from '@/lib/embedded-table/cells';

export interface AdditionalViewTabProps {
  /** Slug widoku dodatkowego (`ext_additional_views.slug`). */
  viewSlug: string;
  /** UUID rekordu-hosta. WYMAGANY do pobrania danych (T22 J1) — backend autoryzuje odczyt
   *  PER-RECORD (`canAccessAdditionalParent`), więc bez konkretnego rekordu nie ma czego
   *  autoryzować. `null`/`undefined` w trybie tworzenia (rekord jeszcze nie ma id) → hook
   *  zostaje `enabled:false`, zakładka pozostaje w stanie pustym do czasu zapisania rekordu
   *  (redirect na URL z id po zapisie naturalnie remountuje ten komponent). */
  recordId: string | null | undefined;
  /** Wartości pól rekordu (ext + core, już scalone przez callera — patrz
   *  `buildAdditionalViewRecordValues`, `lib/additional-views/record-values.ts`). */
  recordValues: Record<string, unknown>;
  /** Układ kolumn umieszczenia widoku na formatce (`element.settings`): kolejność, widoczność,
   *  podpis, szerokość, wyrównanie, miejsca po przecinku. Brak = kolejność zapytania, wszystko
   *  widoczne (jak przed wprowadzeniem układu). */
  columnSettings?: Record<string, unknown>;
}

function formatDateValue(raw: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (!m) return raw;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? raw : d.toLocaleDateString('pl-PL');
}

function formatDatetimeValue(raw: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(raw);
  if (!m) return raw;
  const d = new Date(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4]),
    Number(m[5]),
    m[6] ? Number(m[6]) : 0,
  );
  return Number.isNaN(d.getTime()) ? raw : d.toLocaleString('pl-PL');
}

function formatAmountValue(raw: string): string {
  const n = Number(raw);
  if (Number.isNaN(n)) return raw;
  return n.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Formatuje jedną wartość komórki wg `displayType` — patrz kontrakt formatowania w docstring.
 * Eksportowana — `AdditionalViewFormModal` (Ustawienia → edytor widoku) reużywa ją do
 * podglądu wyniku sekcji „Testuj", żeby nie duplikować reguł formatowania w dwóch miejscach.
 */
export function formatCellValue(
  value: unknown,
  displayType: AdditionalViewColumn['displayType'],
  t: (key: string) => string,
): string {
  if (value === null || value === undefined) return '—';
  switch (displayType) {
    case 'date':
      return formatDateValue(String(value));
    case 'datetime':
      return formatDatetimeValue(String(value));
    case 'amount':
      return formatAmountValue(String(value));
    case 'number':
      return String(value);
    case 'bool':
      return value === true ? t('common.yes') : t('common.no');
    case 'text':
    default:
      return String(value);
  }
}

/** Czy widok ma wymagany parametr, którego wiązanie nie jest jeszcze rozwiązywalne. */
function hasMissingRequiredParams(
  view: AdditionalViewDefinition,
  recordValues: Record<string, unknown>,
): boolean {
  return view.declaredParams.some((param) => {
    if (!param.required) return false;
    const binding = view.paramBindings[param.name];
    if (!binding) return true;
    if (binding.kind !== 'record_field') return false;
    const value = recordValues[binding.fieldSlug];
    return value === null || value === undefined || value === '';
  });
}

/**
 * Wiersz widoku dla `ConfigurableTable` — obiekt kluczowany po INDEKSIE kolumny
 * (`col-<i>`), NIE po nazwie: nazwy kolumn z dowolnego SELECT-a mogą się duplikować
 * (`SELECT a.status, b.status`), a klucz-obiekt collapsowałby duplikaty. `__rowIndex`
 * daje stabilny, unikalny klucz wiersza (`getRowId`) — wynik SELECT-a nie ma własnego id.
 */
export type AdditionalViewRow = { __rowIndex: number } & Record<string, unknown>;

/** `displayType` kolumny widoku → kategoria filtra nagłówka `ConfigurableTable`. */
export function additionalViewFilterCategory(
  displayType: AdditionalViewColumn['displayType'],
): ColumnFilterCategory {
  switch (displayType) {
    case 'amount':
    case 'number':
      return 'number';
    case 'date':
    case 'datetime':
      return 'date';
    case 'bool':
      return 'boolean';
    case 'text':
    default:
      return 'text';
  }
}

/**
 * Zamienia pozycyjne wiersze widoku (`unknown[][]`) na obiekty kluczowane po indeksie
 * kolumny (`col-<i>`) — patrz `AdditionalViewRow`. Zachowuje duplikaty nazw kolumn.
 */
export function toAdditionalViewRows(
  columns: AdditionalViewColumn[],
  rows: unknown[][],
): AdditionalViewRow[] {
  return rows.map((row, rowIndex) => {
    const obj: AdditionalViewRow = { __rowIndex: rowIndex };
    columns.forEach((_col, colIndex) => {
      obj[`col-${colIndex}`] = row[colIndex];
    });
    return obj;
  });
}

/** Miejsca po przecinku kwot (kolumny dziesiętne), gdy układ formatki ich nie ustawia. */
const DEFAULT_AMOUNT_DECIMALS = 2;

/**
 * Formatuje komórkę widoku z uwzględnieniem układu: kolumna dziesiętna (`amount`) dostaje liczbę
 * miejsc z układu (domyślnie 2 — jak dotąd), formatowaną dokładnie z tekstu; wartość pusta albo
 * nieliczbowa idzie dotychczasową ścieżką (`formatCellValue`: „—" / surowy tekst). Pozostałe rodzaje
 * — bez zmian.
 */
function formatViewCell(
  value: unknown,
  col: AdditionalViewColumn,
  decimals: number | undefined,
  isDecimalKind: boolean,
  t: (key: string) => string,
): string {
  if (isDecimalKind && value !== null && value !== undefined) {
    const exact = formatDecimalCell(value, decimals ?? DEFAULT_AMOUNT_DECIMALS);
    if (exact !== null) return exact;
  }
  return formatCellValue(value, col.displayType, t);
}

/**
 * Buduje kolumny `ConfigurableTable` z metadanych widoku i układu formatki (`columnSettings` =
 * `element.settings` umieszczenia widoku). Kolejność, widoczność, własny podpis, szerokość startowa,
 * wyrównanie i miejsca po przecinku rozwiązuje `resolveEmbeddedTableColumns` (`@saas/shared`);
 * zwracamy WYŁĄCZNIE kolumny widoczne, w kolejności układu. Akcesor zostaje pozycyjny
 * (`col-<indeks kolumny w wyniku zapytania>`) — układ zmienia tylko to, co i jak rysujemy.
 * Bez układu: kolejność zapytania, wszystko widoczne, nagłówek = nazwa kolumny (dana z SQL, nie
 * i18n), kwoty z 2 miejscami. `dataType:'number'` włącza sort numeryczny; kategoria filtra z
 * `displayType`. Opcje zakładki „Wartości" liczymy z pobranych wierszy (te same formaty co komórki)
 * — endpoint podpowiedzi nie zna syntetycznego id widoku, więc bez nich lista byłaby pusta.
 */
export function buildAdditionalViewColumns(
  columns: AdditionalViewColumn[],
  rows: unknown[][],
  t: (key: string) => string,
  columnSettings?: Record<string, unknown>,
): ColumnDefinition<AdditionalViewRow>[] {
  const descriptors = additionalViewDescriptors(columns);
  const indexById = new Map<string, number>(
    descriptors.map((descriptor, index) => [descriptor.id, index] as const),
  );
  const resolved = resolveEmbeddedTableColumns(descriptors, columnSettings);

  const built: ColumnDefinition<AdditionalViewRow>[] = [];
  for (const layoutColumn of resolved) {
    if (!layoutColumn.visible) continue;
    const colIndex = indexById.get(layoutColumn.id);
    const col = colIndex === undefined ? undefined : columns[colIndex];
    // Układ rozwiązuje się z tych samych deskryptorów — brak kolumny to błąd kontraktu.
    if (colIndex === undefined || col === undefined) {
      throw new Error(
        `buildAdditionalViewColumns: brak kolumny wyniku dla id „${layoutColumn.id}"`,
      );
    }
    const key = `col-${colIndex}`;
    const category = additionalViewFilterCategory(col.displayType);
    const isNumeric = col.displayType === 'amount' || col.displayType === 'number';
    const isDecimalKind = layoutColumn.descriptor.kind === 'decimal';
    const format = (value: unknown): string =>
      formatViewCell(value, col, layoutColumn.decimals, isDecimalKind, t);

    // Distinct wartości do zakładki „Wartości" (podpowiadamy z danych — bez sieci).
    const seen = new Set<string>();
    const options: Array<{ value: string; label: string }> = [];
    for (const row of rows) {
      const raw = row[colIndex];
      if (raw == null) continue;
      const value = String(raw);
      if (seen.has(value)) continue;
      seen.add(value);
      options.push({ value, label: format(raw) });
    }

    built.push({
      id: key,
      accessor: key,
      header: layoutColumn.label ?? col.name,
      sortable: true,
      align: effectiveColumnAlign(layoutColumn),
      dataType: isNumeric ? 'number' : 'text',
      ...(layoutColumn.width !== undefined ? { defaultWidth: layoutColumn.width } : {}),
      // Prosty input „zawiera" w wierszu filtrów tylko dla tekstu; liczby/daty/tak-nie
      // filtrują się przez popup nagłówka / dedykowaną kontrolkę wiersza filtrów.
      filterable: category === 'text',
      renderCell: (value: unknown) => format(value),
      headerFilter: {
        category,
        ...(options.length > 0 ? { options } : {}),
      },
    });
  }
  return built;
}

/**
 * Sygnatura układu zbudowanych kolumn (id, nagłówek, wyrównanie, szerokość startowa) — klucz tabeli. `useTableConfig`
 * liczy kolejność/widoczność/szerokość raz (bez zapisanego układu nie przelicza ich po zmianie
 * definicji kolumn), więc zmiana układu formatki musi przemontować tabelę; odświeżenie samych
 * wierszy — nie (rozciągnięcie kolumny w sesji zostaje).
 */
function layoutSignature(columns: ColumnDefinition<AdditionalViewRow>[]): string {
  return columns
    .map((c) => `${c.id}:${c.header}:${c.align ?? ''}:${c.defaultWidth ?? ''}`)
    .join('|');
}

export function AdditionalViewTab({
  viewSlug,
  recordId,
  recordValues,
  columnSettings,
}: AdditionalViewTabProps) {
  const { t } = useTranslation();

  const viewsQuery = useAdditionalViewsList();
  const view = viewsQuery.data?.find((v) => v.slug === viewSlug);

  // Zatwierdzony snapshot recordValues wzięty przy mouncie — patrz nagłówek pliku.
  const [committed, setCommitted] = useState<Record<string, unknown>>(recordValues);

  // Rules-of-hooks: `useAdditionalViewData` musi być wołane bezwarunkowo (przed early-returny
  // niżej dla stanu ładowania/braku widoku) — brak `view` przekłada się na `enabled:false`
  // (viewId `undefined`) zamiast pominięcia wywołania hooka. `recordId` idzie jako osobny
  // argument (query param po stronie backendu, autoryzowany per-record) — NIE w body (T22 J1,
  // `ExecuteAdditionalViewDataInput` już go nie niesie). Brak `recordId` (tryb tworzenia) →
  // hook sam ustawia `enabled:false`.
  const missingRequired = view ? hasMissingRequiredParams(view, committed) : true;
  const dataResult = useAdditionalViewData(
    view?.id,
    recordId,
    { recordValues: buildAdditionalViewRecordValues(committed) },
    Boolean(view) && !missingRequired,
  );

  function handleRefresh() {
    setCommitted(recordValues);
  }

  const tableColumns = useMemo(
    () =>
      dataResult.data
        ? buildAdditionalViewColumns(
            dataResult.data.columns,
            dataResult.data.rows,
            t,
            columnSettings,
          )
        : [],
    [dataResult.data, t, columnSettings],
  );
  // Wynik ma kolumny, ale układ formatki ukrył je wszystkie (wynik bez kolumn to nie to samo).
  const allColumnsHidden = (dataResult.data?.columns.length ?? 0) > 0 && tableColumns.length === 0;
  const tableRows = useMemo(
    () =>
      dataResult.data ? toAdditionalViewRows(dataResult.data.columns, dataResult.data.rows) : [],
    [dataResult.data],
  );

  if (viewsQuery.isPending) {
    return (
      <div className="p-4 text-sm text-text-muted" data-testid="additional-view-loading">
        {t('common.loading')}
      </div>
    );
  }

  if (!view) {
    return (
      <div className="p-4 text-sm text-text-tertiary" data-testid="additional-view-not-found">
        {t('additionalViews.grid.viewNotFound', { slug: viewSlug })}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="additional-view-tab">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-text-primary">{view.displayName}</h4>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
          onClick={handleRefresh}
          disabled={missingRequired || dataResult.isFetching}
          data-testid="additional-view-refresh-btn"
        >
          {t('additionalViews.grid.refresh')}
        </Button>
      </div>

      {missingRequired && (
        <p className="text-sm text-text-tertiary" data-testid="additional-view-missing-params">
          {t('additionalViews.grid.missingRequiredParams')}
        </p>
      )}

      {!missingRequired && dataResult.isLoading && (
        <div className="flex justify-center py-6" data-testid="additional-view-data-loading">
          <Spinner />
        </div>
      )}

      {!missingRequired && dataResult.isError && (
        <p className="text-sm text-red-600" data-testid="additional-view-data-error">
          {t('additionalViews.grid.loadError')}
        </p>
      )}

      {!missingRequired && !dataResult.isLoading && !dataResult.isError && dataResult.data && (
        <>
          {allColumnsHidden ? (
            // Układ formatki nie ma żadnej widocznej kolumny — tabela bez kolumn nic by nie pokazała.
            <p
              className="text-sm text-text-tertiary"
              data-testid="additional-view-all-columns-hidden"
            >
              {t('additionalViews.grid.allColumnsHidden')}
            </p>
          ) : (
            <ConfigurableTable<AdditionalViewRow>
              key={layoutSignature(tableColumns)}
              tableId={`additional-view-${view.id}`}
              columns={tableColumns}
              data={tableRows}
              getRowId={(row) => String(row.__rowIndex)}
              persistConfig={false}
              configurable={false}
              showViewModeToggle={false}
              testIdPrefix="additional-view"
              emptyIcon={LayoutGrid}
              emptyTitle={t('additionalViews.grid.empty')}
              emptyDescription=""
            />
          )}
          {dataResult.data.truncated && (
            <p className="text-xs text-text-muted" data-testid="additional-view-truncated">
              {t('additionalViews.grid.truncated')}
            </p>
          )}
        </>
      )}
    </div>
  );
}
